/**
 * Puschies Such-Drawer (Port aus Hyper snippets/search-drawer.liquid, Inline-Script).
 * Ab 2 Zeichen (200 ms Debounce) Ergebnisse per Section Rendering API laden
 * (/search/suggest?…&section_id=p-predictive-search), sonst leerer Zustand.
 * `.is-typing` am <theme-drawer> blendet leeren Zustand aus und Ergebnisse + X ein (wie Hyper).
 * Öffnen/Schließen/Overlay übernimmt <theme-drawer> (assets/theme-drawer.js).
 */
class PSearchForm extends HTMLElement {
  /** @type {number | undefined} */
  #timer;
  #lastQuery = '';
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.drawer = this.closest('theme-drawer');
    this.input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-p-search-input]'));
    this.results = this.querySelector('[data-p-search-results]');
    this.clearButton = this.querySelector('[data-p-search-clear]');
    if (!this.input || !this.results) return;

    this.input.addEventListener('input', this.#onInput);
    this.clearButton?.addEventListener('click', this.#onClear);
    // Wie Hyper: Eingabefeld nach dem Öffnen fokussieren
    this.drawer?.addEventListener('theme-drawer:open', this.#onOpen);
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.#onInput);
    this.clearButton?.removeEventListener('click', this.#onClear);
    this.drawer?.removeEventListener('theme-drawer:open', this.#onOpen);
    this.#abort?.abort();
  }

  #onOpen = () => {
    setTimeout(() => this.input?.focus({ preventScroll: true }), 80);
  };

  #onInput = () => {
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.#search(), 200);
  };

  #onClear = () => {
    if (!this.input) return;
    this.input.value = '';
    this.#reset();
    this.input.focus();
  };

  /** @param {boolean} typing */
  #setTyping(typing) {
    this.drawer?.classList.toggle('is-typing', typing);
  }

  #reset() {
    this.#abort?.abort();
    this.#lastQuery = '';
    this.#setTyping(false);
    this.results?.replaceChildren();
  }

  async #search() {
    if (!this.input || !this.results) return;
    const query = this.input.value.trim();
    if (query === this.#lastQuery) return;
    this.#lastQuery = query;

    if (query.length < 2) {
      this.#reset();
      return;
    }
    this.#setTyping(true);

    this.#abort?.abort();
    const abort = new AbortController();
    this.#abort = abort;

    const url = new URL(this.dataset.url || '/search/suggest', window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('section_id', this.dataset.section || 'p-predictive-search');

    try {
      const response = await fetch(url, { signal: abort.signal });
      if (!response.ok) throw new Error(String(response.status));
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('[data-p-search-results-content]');
      if (abort.signal.aborted) return;
      this.results.replaceChildren(...(content ? [document.importNode(content, true)] : []));
    } catch (error) {
      if (abort.signal.aborted) return;
      const message = document.createElement('p');
      message.className = 'p-search-drawer__no-results';
      message.textContent = this.dataset.error || '';
      this.results.replaceChildren(message);
    }
  }
}

if (!customElements.get('p-search-form')) {
  customElements.define('p-search-form', PSearchForm);
}
